import { useParams } from '@remix-run/react';
import type { PostData, TPostElement } from '@shared/dream-components';
import { PostElement } from '@shared/dream-components';
import { type ReactNode, useMemo } from 'react';

import usePaginatedPosts from '~/hooks/usePaginatedPosts';

import ClientSideRenderer from '../ClientSideRenderer';

import useRecommendedPosts from '~/hooks/useLoaders/useRecommendedPosts';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PostLoaderData } from '~/routes/p/$slug';

interface Props {
  node: TPostElement;
  children: ReactNode | ReactNode[];
}

const getArchiveOffsetPosts = (posts: PostData[], postsOffset: number) => {
  const postsCopy = [...posts];
  const offsetPosts = postsCopy.slice(postsOffset, postsOffset + posts.length);
  return offsetPosts;
};

const Post = ({ node, children }: Props) => {
  const {
    hasFakeData,
    data: { posts: defaultPosts, pagination },
    shouldScrollHorizontally,
    postGroupType,
    postsOffset = 0,
    postsCategory,
  } = node.attrs;
  const isTagPage = postGroupType === 'tag_page';
  const isAuthorPage = postGroupType === 'author_page';
  const isRecommendationsBlock = postGroupType === 'recommendations';
  const isArchive = postGroupType === 'archive';
  const { tag } = useParams();

  const tags = useMemo(() => {
    if (isTagPage && tag) {
      return [tag];
    }

    if (postsCategory) {
      return [postsCategory];
    }

    return [];
  }, [isTagPage, tag, postsCategory]);

  const { post } = useTypedLoaderData() as PostLoaderData;

  const {
    posts,
    showLoadMore,
    loadMorePosts,
    isLoading,
    resetSearchState,
    searchQuery,
    isSearching,
    setIsSearching,
    handleDebouncedSearchQuery,
    handleNonDebouncedSearchQuery,
  } = usePaginatedPosts({
    defaultPosts: isArchive ? getArchiveOffsetPosts(defaultPosts, postsOffset) : defaultPosts,
    pagination: pagination || null,
    enabled: hasFakeData === false || isTagPage || isAuthorPage || !isRecommendationsBlock,
    postGroupType,
    hasFakeData: hasFakeData || false,
    tags,
  });

  const recommendedPosts = useRecommendedPosts(post?.id, isRecommendationsBlock);
  const recommendedPostsAsPosts = recommendedPosts?.map(
    (recommendedPost) =>
      ({
        id: recommendedPost.id,
        web_title: recommendedPost.web_title,
        web_subtitle: recommendedPost.web_subtitle,
        comments_state: '',
        override_scheduled_at: recommendedPost.override_scheduled_at,
        scheduled_at: '',
        audience: recommendedPost.audience,
        is_premium: recommendedPost.audience === 'premium',
        slug: recommendedPost.slug,
        content_tags: recommendedPost.content_tags,
        image_url: recommendedPost.image_url,
        authors: recommendedPost.authors,
        estimated_reading_time: recommendedPost.estimated_reading_time,
        estimated_reading_time_display: recommendedPost.estimated_reading_time_display,
      }) as PostData,
  );

  // If on post page, exclude that post in the list of posts
  const renderablePosts = pagination ? posts : defaultPosts;
  const filteredPosts = post ? renderablePosts.filter(({ id }) => post.id !== id) : renderablePosts;

  return (
    <ClientSideRenderer shouldRenderClientSide={Boolean(shouldScrollHorizontally)}>
      <PostElement
        element={{
          ...node,
          attrs: {
            ...node.attrs,
            data: isRecommendationsBlock
              ? { posts: recommendedPostsAsPosts }
              : {
                  ...node.attrs.data,
                  posts: filteredPosts,
                },
          },
        }}
        isLivePost
        paginateHasMorePosts={showLoadMore}
        paginateFetchMorePosts={() => loadMorePosts(searchQuery || '')}
        isLoading={isLoading}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        handleNonDebouncedSearchQuery={handleNonDebouncedSearchQuery}
        handleResetSearchState={resetSearchState}
        onSearchQueryChange={(query) => handleDebouncedSearchQuery(query || '')}
      >
        {children}
      </PostElement>
    </ClientSideRenderer>
  );
};

export default Post;
